import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createSupportTicket } from '../../services/supportTicketApi';
import TicketAttachmentField from '../support/TicketAttachmentField';

const ISSUE_TYPES = [
  { value: 'technical_problem', label: 'Technical Problem' },
  { value: 'something_doesnt_feel_right', label: "Something Doesn't Feel Right" },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' }
];

const SupportTicketDialog = ({
  open,
  onClose,
  token,
  currentPage = 'chat',
  chatPhase,
  conversationId,
  currentPersona,
  /** Optional extra context the calling surface knows about — e.g. the
   *  in-call therapist UI passes liveCallId + connection state, the
   *  scenario editor could pass scenarioId, etc. Merged on top of the
   *  defaults so the caller can also override journeyStage / userRole. */
  extraContext = {},
  /** Optional human-readable label of where the report is being made from
   *  (e.g. "Clients › Client detail › Goals"). Shown to the reporter so they
   *  can see what context will be attached to the ticket. */
  reportingFrom,
}) => {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);
  // A screenshot is often the whole report: "the button did nothing" and a
  // picture of the button doing nothing are not the same evidence, and testers
  // were describing what they could see because there was nowhere to put the
  // thing itself (ST-20260921-0002).
  const [attachment, setAttachment] = useState(null);

  const getJourneyStage = () => {
    if (extraContext.journeyStage) return extraContext.journeyStage;
    if (chatPhase === 'onboarding') return 'onboarding';
    if (chatPhase === 'main') return 'therapy';
    if (currentPage === 'dashboard' || currentPage === 'journal') return 'therapy';
    return 'unknown';
  };

  // Auto-collected browser / route context — useful for video and
  // rendering bugs ('it crashes on Safari', 'can't scroll on tablet').
  const collectAutoContext = () => {
    if (typeof window === 'undefined') return {};
    const out = {};
    try {
      out.userAgent = window.navigator?.userAgent || undefined;
      if (window.screen?.width && window.screen?.height) {
        out.screenSize = `${window.screen.width}x${window.screen.height}`;
      }
      out.route = `${window.location.pathname}${window.location.search || ''}`;
    } catch (_) {}
    return out;
  };

  const handleSubmit = async () => {
    if (!issueType) {
      setError('Please select an issue type.');
      return;
    }
    if (!description.trim()) {
      setError('Please describe the issue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await createSupportTicket({
        issueType,
        description: description.trim(),
        // Omitted entirely when there is none, so the server sees no key rather
        // than an empty object it has to interpret.
        ...(attachment ? { attachment } : {}),
        context: {
          currentPage,
          journeyStage: getJourneyStage(),
          conversationId: conversationId || undefined,
          personaKey: currentPersona || undefined,
          ...collectAutoContext(),
          // Caller-provided context wins over auto-collected so e.g. an
          // in-call surface can override route with a richer label.
          ...extraContext,
        }
      }, token);

      setSuccessInfo(result.ticket);
    } catch (err) {
      const msg = err.response?.data?.msg || err.message || 'Failed to submit ticket.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setIssueType('');
    setDescription('');
    setLoading(false);
    setError('');
    setSuccessInfo(null);
    // Without this a file chosen for one report stays attached to the next one
    // the reporter opens, which would quietly send the wrong screenshot.
    setAttachment(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Get Help / Report an Issue
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {successInfo ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your ticket has been submitted successfully.
            </Alert>
            <Typography variant="h6" gutterBottom>
              Ticket Number: {successInfo.ticketNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We'll look into this and get back to you. You can reference this
              ticket number if you need to follow up.
            </Typography>
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {reportingFrom && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 2 }}
                data-testid="support-ticket-reporting-from"
              >
                Reporting from: {reportingFrom}
              </Typography>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="issue-type-label">What kind of issue?</InputLabel>
              <Select
                labelId="issue-type-label"
                value={issueType}
                label="What kind of issue?"
                onChange={(e) => setIssueType(e.target.value)}
              >
                {ISSUE_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Describe the issue"
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              helperText={`${description.length}/2000 characters`}
            />

            <TicketAttachmentField
              attachment={attachment}
              onChange={setAttachment}
              onError={setError}
              disabled={loading}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        {successInfo ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !issueType || !description.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SupportTicketDialog;
